import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  ColorPicker,
  TimePicker,
  DatePicker,
  Input,
  message,
  Row,
  Col,
  Upload,
  Checkbox,
} from "antd";
import upload from "../../../Assests/Images/upload.svg";
import dayjs from "dayjs";
import { CloseCircleFilled } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { eventAPI, clientAPI } from "../../../utils/api";

import "./style.css";
import { ClipLoader } from "react-spinners";
const { TextArea } = Input;

const AddNewEvent = () => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = location?.state?.eventId;
  const [eventStatus, setEventStatus] = useState(null);
  // Whether the 25%+ deposit has already been met — hides the deposit-required
  // note once a booking is actually confirmed. Only meaningful in edit mode;
  // a brand-new (unsaved) event has no deposit yet.
  const [depositMet, setDepositMet] = useState(false);
  const [originalFiles, setOriginalFiles] = useState([]); // backend images
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const MEDIA_URL = process.env.REACT_APP_MEDIA_URL;

  // Dates this client already has an event on (excluding the event being
  // edited, if any) — surfaced on the date pickers so re-booking the same
  // date doesn't happen by accident.
  const [clientEventDates, setClientEventDates] = useState(new Set());

  const loadClientEventDates = async (clientId) => {
    if (!clientId) {
      setClientEventDates(new Set());
      return;
    }
    try {
      const response = await eventAPI.getClientEvents(clientId);
      const events = response?.results?.data || [];
      const dates = new Set();
      events.forEach((ev) => {
        if (eventId && String(ev.id) === String(eventId)) return;
        if (ev.status === "cancel") return;
        let cursor = dayjs(ev.start_date);
        const end = dayjs(ev.end_date || ev.start_date);
        while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
          dates.add(cursor.format("YYYY-MM-DD"));
          cursor = cursor.add(1, "day");
        }
      });
      setClientEventDates(dates);
    } catch (error) {
      console.error("Failed to load client's existing event dates:", error);
    }
  };

  const bookedDateCellRender = (current, info) => {
    if (info?.type !== "date") return info?.originNode;
    const isBooked = clientEventDates.has(current.format("YYYY-MM-DD"));
    return (
      <div
        className="ant-picker-cell-inner"
        title={isBooked ? "This client already has an event on this date" : undefined}
        style={
          isBooked
            ? {
                backgroundColor: "#fff1b8",
                border: "1px solid #d4b106",
                borderRadius: "50%",
              }
            : undefined
        }
      >
        {current.date()}
      </div>
    );
  };

  const services = [
    "Photography",
    "Charger Plates",
    "Nikah Partition",
    "Picture Sign",
    "Walkway Decor",
    "Bridal Entrance Flower Chader",
    "Photo Booth",
    "Guest Management",
    "Wedding Room Decor",
    "Customize Decor",
    "Home Decor",
    "Wedding Car Decor",
    "Fountain Welcome Sign",
    "Table Covers",
    "Fireworks Machine",
    "None",
  ];

  // Load event data if editing
  useEffect(() => {
    if (eventId) {
      const loadEvent = async () => {
        try {
          setLoading(true);
          const response = await eventAPI.getEventDetail(eventId);
          const backendFiles = response.data.reference_images?.map((img) => ({
            uid: `backend-${img.id}`,
            name: img.image.split("/").pop(), // filename
            status: "done",
            url: img.image, // used to display existing images
            id: img.id, // save backend id for removal
          }));

          setFileList(backendFiles);
          setOriginalFiles(backendFiles);

          const selectedServicesBackend = response?.data?.addons
            ?.filter((addon) => addon.is_selected)
            ?.map((addon) => addon.name);
          setEventStatus(response?.data?.status);
          setDepositMet(Boolean(response?.data?.deposit_met));

          form.setFieldsValue({
            client: response?.data?.client,
            category: response?.data?.category,
            color: response?.data?.color,
            eventName: response?.data?.name,
            eventTime: response?.data?.event_time
              ? dayjs(response.data.event_time, "HH:mm:ss")
              : null,
            eventStartDate: response?.data?.start_date
              ? dayjs(response.data.start_date, "YYYY-MM-DD")
              : null,
            eventEndDate: response?.data?.end_date
              ? dayjs(response.data.end_date, "YYYY-MM-DD")
              : null,
            setupStartTime: response?.data?.setup_start_time
              ? dayjs(response.data.setup_start_time, "HH:mm:ss")
              : null,
            tearDownTime: response?.data?.teardown_time
              ? dayjs(response.data.teardown_time, "HH:mm:ss")
              : null,
            eventPackage: response?.data?.package,
            selectedServices: selectedServicesBackend || [],
            description: response?.data?.description,
            amount: response?.data?.value,
            venue_address:
              response?.data?.venue_address || response?.data?.address || "",
          });

          if (response?.data?.client) {
            loadClientEventDates(response.data.client);
          }
        } catch (error) {
          messageApi.error("Failed to load event details");
        } finally {
          setLoading(false);
        }
      };
      loadEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, form, messageApi]);

  // Load clients and packages on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const clientsResponse = await clientAPI.getClients("");
        setClients(clientsResponse.results.data || []);

        const packagesResponse = await eventAPI.getPackages();
        setPackages(packagesResponse.data || []);
      } catch (error) {
        messageApi.error("Failed to load data");
        console.error("Load data error:", error);
      }
    };
    loadData();
  }, [messageApi]);

  function getRandomVibrantHexColor() {
    const hue = Math.floor(Math.random() * 360); // full color wheel
    const saturation = 90 + Math.random() * 10; // 90–100%
    const lightness = 45 + Math.random() * 10; // 45–55%

    return hslToHex(hue, saturation, lightness);
  }

  function hslToHex(h, s, l) {
    s /= 100;
    l /= 100;

    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) =>
      l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

    return (
      "#" +
      [f(0), f(8), f(4)]
        .map((x) =>
          Math.round(x * 255)
            .toString(16)
            .padStart(2, "0"),
        )
        .join("")
    );
  }

  const onFinish = async (values) => {
    const colorHex = getRandomVibrantHexColor();

    const formData = new FormData();

    try {
      setLoading(true);

      formData.append("name", values.eventName);
      formData.append("description", values.description || "");
      formData.append("category", values.category);
      formData.append("package", values.eventPackage);
      formData.append("client", values.client);
      formData.append("start_date", values.eventStartDate.format("YYYY-MM-DD"));
      formData.append("end_date", values.eventEndDate.format("YYYY-MM-DD"));
      formData.append("event_time", values.eventTime.format("HH:mm:ss"));
      formData.append("color", colorHex);
      formData.append("value", parseFloat(values.amount) || 0);
      formData.append("venue_address", values.venue_address || "");

      if (!eventId && values.advance_payment) {
        formData.append(
          "advance_payment_amount",
          parseFloat(values.advance_payment_amount) || 0,
        );
      }

      formData.append(
        "setup_start_time",
        values.setupStartTime.format("HH:mm:ss"),
      );
      formData.append("teardown_time", values.tearDownTime.format("HH:mm:ss"));
      // New bookings always start as In Query — the backend promotes to
      // Booked automatically once a deposit actually reaches the 25%
      // threshold (see the Payment post_save signal), rather than trusting
      // the "Advance payment received" checkbox alone regardless of amount.
      const computedStatus = eventStatus ?? "in_query";

      formData.append("status", computedStatus);

      if (removedImageIds.length > 0) {
        formData.append("removed_images", JSON.stringify(removedImageIds));
      }

      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append("images", file.originFileObj);
        }
      });

      if (eventId) {
        const response = await eventAPI.updateEvent(eventId, formData);

        if (values.selectedServices && values.selectedServices.length > 0) {
          const addons = values.selectedServices.map((service) => ({
            name: service,
            price: 0,
            is_selected: true,
          }));
          await eventAPI.addEventAddons(response.data.id, addons);
        }

        messageApi.success("Event added successfully!");
        form.resetFields();
        setFileList([]);
        const returnPage = location?.state?.returnPage;
        navigate(returnPage ? `/event?page=${returnPage}` : "/event");
      } else {
        const response = await eventAPI.createEvent(formData);

        if (values.selectedServices && values.selectedServices.length > 0) {
          const addons = values.selectedServices.map((service) => ({
            name: service,
            price: 0,
            is_selected: true,
          }));
          await eventAPI.addEventAddons(response.data.id, addons);
        }

        messageApi.success("Event added successfully!");
        form.resetFields();
        setFileList([]);
        navigate("/event");
      }
    } catch (error) {
      messageApi.error(error.message || "Failed to add event!");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    multiple: true,
    fileList,
    beforeUpload: () => false,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
  };

  const handleRemoveFile = (file, e) => {
    e.stopPropagation();

    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    if (file.id) {
      setRemovedImageIds((prev) => [...prev, file.id]);

      setOriginalFiles((prev) => prev.filter((f) => f.id !== file.id));
    }

    messageApi.success(`${file.name} removed`);
  };

  const advancePayment = Form.useWatch("advance_payment", form);

  return loading ? (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      className="pdf-loader-overlay"
    >
      <ClipLoader size={60} color="#e2ae03" />
    </div>
  ) : (
    <div className="new-event">
      {contextHolder}
      <div className="form-heading">Select Client</div>
      <div className="sub-heading">Select Client</div>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="client"
          rules={[{ required: true, message: "Please select a client!" }]}
        >
          <Select
            placeholder="Select a client"
            disabled={Boolean(eventId)}
            onChange={(value) => {
              const selected = clients.find((client) => client.id === value);
              // Only auto-fill when the venue field is still untouched —
              // don't clobber a venue address the user already customized
              // for this event just because they changed the client.
              const currentVenue = form.getFieldValue("venue_address");
              if (!currentVenue) {
                form.setFieldsValue({ venue_address: selected?.address || "" });
              }
              loadClientEventDates(value);
            }}
          >
            {clients.map((client) => (
              <Select.Option key={client.id} value={client.id}>
                {client.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <div className="sub-heading">Venue Address</div>
        <Form.Item
          name="venue_address"
          rules={[{ required: true, message: "Please enter the venue address!" }]}
          extra="Defaults to the client's saved address — edit this if the event is at a different venue."
        >
          <TextArea
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder="Where is this event taking place?"
          />
        </Form.Item>
        <div className="form-heading">Event Information</div>
        <div className="item-wrapper">
          <div className="item">
            <div className="sub-heading">Event Category</div>
            <Form.Item
              name="category"
              rules={[{ required: true, message: "Please select a category!" }]}
              style={{ margin: 0 }}
            >
              <Select placeholder="Select Category">
                <Select.Option value="wedding">Wedding</Select.Option>
                <Select.Option value="birthday">Birthday</Select.Option>
                <Select.Option value="corporate">Corporate</Select.Option>
                <Select.Option value="conference">Conference</Select.Option>
                <Select.Option value="seminar">Seminar</Select.Option>
                <Select.Option value="exhibition">Exhibition</Select.Option>
                <Select.Option value="workshop">Workshop</Select.Option>
                <Select.Option value="other">Other</Select.Option>
              </Select>
            </Form.Item>
          </div>
        </div>
        <div className="item-wrapper">
          <div className="item">
            <div className="sub-heading">Event Name</div>
            <Form.Item
              name="eventName"
              rules={[
                { required: true, message: "Please enter the event name!" },
              ]}
              style={{ margin: 0 }}
            >
              <Input
                className="ant-picker"
                type="text"
                placeholder="Enter Event Name"
              />
            </Form.Item>
          </div>

          <div className="item">
            <div className="sub-heading">Event Time</div>
            <Form.Item
              name="eventTime"
              rules={[
                { required: true, message: "Please select the event time!" },
              ]}
              style={{ margin: 0 }}
            >
              <TimePicker
                use12Hours
                format="h:mm A"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
        </div>
        <div className="item-wrapper">
          <div className="item">
            <div className="sub-heading">Event Start Date</div>
            <Form.Item
              name="eventStartDate"
              rules={[
                { required: true, message: "Please select the start date!" },
              ]}
              style={{ margin: 0 }}
            >
              <DatePicker
                defaultValue={dayjs()}
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                cellRender={bookedDateCellRender}
              />
            </Form.Item>
          </div>

          <div className="item">
            <div className="sub-heading">Event End Date</div>
            <Form.Item
              name="eventEndDate"
              rules={[
                { required: true, message: "Please select the end date!" },
              ]}
              style={{ margin: 0 }}
            >
              <DatePicker
                defaultValue={dayjs()}
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                cellRender={bookedDateCellRender}
              />
            </Form.Item>
          </div>
        </div>
        {clientEventDates.size > 0 && (
          <div style={{ fontSize: 12, color: "#ad8b00", marginTop: -8, marginBottom: 8 }}>
            Tip: dates circled in the calendar above already have an event booked for this client.
          </div>
        )}
        <div className="set-up-time">
          <div className="form-heading">Setup & Teardown Time</div>
          <div className="item-wrapper">
            <div className="item">
              <div className="sub-heading">Setup Start Time</div>
              <Form.Item
                name="setupStartTime"
                rules={[
                  {
                    required: true,
                    message: "Please select the setup start time!",
                  },
                ]}
                style={{ margin: 0 }}
              >
                <TimePicker
                  use12Hours
                  format="h:mm A"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>

            <div className="item">
              <div className="sub-heading">Tear Down Time</div>
              <Form.Item
                name="tearDownTime"
                rules={[
                  {
                    required: true,
                    message: "Please select the tear down time!",
                  },
                ]}
                style={{ margin: 0 }}
              >
                <TimePicker
                  use12Hours
                  format="h:mm A"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>
          </div>
        </div>

        <div className="item-wrapper">
          <div className="item">
            <div className="sub-heading">Package</div>
            <Form.Item
              name="eventPackage"
              rules={[{ required: true, message: "Please select a package!" }]}
              style={{ margin: 0 }}
            >
              <Select placeholder="Select Package">
                {packages.map((pkg) => (
                  <Select.Option key={pkg.id} value={pkg.id}>
                    {pkg.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="item">
            <div className="sub-heading">Upload</div>

            <Form.Item className="custom-upload" name="packageUpload">
              <Upload
                {...uploadProps}
                accept=".jpg,.jpeg,.png"
                showUploadList={false}
              >
                <div className="upload-dropzone">
                  <img src={upload} alt="upload" className="upload-icon" />

                  <div className="placeholder">
                    {fileList.length > 0 ? (
                      <div className="file-list">
                        {fileList.map((file) => (
                          <div className="file-preview" key={file.uid}>
                            <img
                              src={
                                file.originFileObj
                                  ? URL.createObjectURL(file.originFileObj)
                                  : `${MEDIA_URL}${file.url}`
                              }
                              alt={file.name}
                            />

                            <CloseCircleFilled
                              className="remove-icon"
                              onClick={(e) => handleRemoveFile(file, e)}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      "Upload Reference Images"
                    )}
                  </div>
                </div>
              </Upload>
            </Form.Item>
          </div>
        </div>
        <div className="package-add">
          <div className="sub-heading">Package Add On Details</div>
          <Form.Item
            name="selectedServices"
            rules={[
              {
                required: false,
                message: "Please select at least one service!",
              },
            ]}
          >
            <Checkbox.Group style={{ width: "100%" }}>
              <Row gutter={[7, 6]}>
                {services.map((service, index) => (
                  <Col key={index}>
                    <Checkbox value={service}>{service}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </div>
        <div className="description">
          <div className="description-title">Description</div>
          <Form.Item name="description">
            <TextArea autoSize={{ minRows: 4, maxRows: 8 }} />
          </Form.Item>
        </div>
        <div className="invoice">
          <div className="form-heading">Invoice Amount</div>
          {!depositMet && eventStatus !== "cancel" && (
            <div
              style={{
                fontSize: 12,
                color: "#666",
                background: "#f7fbff",
                border: "0.83px solid var(--input-stroke, #d4d7e3)",
                borderRadius: 10,
                padding: "10px 12px",
                marginBottom: 12,
              }}
            >
              A deposit of at least 25% of the event value is required to confirm a booking, and
              the remaining balance must be fully cleared one week before the event date.
            </div>
          )}
          <div className="item">
            <div className="sub-heading">Enter Amount</div>
            <Form.Item
              name="amount"
              rules={[{ required: true, message: "Please enter the amount!" }]}
              style={{ margin: 0 }}
            >
              <Input
                className="ant-picker"
                type="number"
                placeholder="Event amount"
              />
            </Form.Item>
          </div>

          {/* Initial deposit — only captured at booking time; further
              payments are recorded on the event's Payment History instead. */}
          {!eventId && (
            <>
              <Form.Item
                name="advance_payment"
                valuePropName="checked"
                initialValue={false}
              >
                <Checkbox>Advance payment received</Checkbox>
              </Form.Item>

              {advancePayment && (
                <Form.Item
                  name="advance_payment_amount"
                  rules={[
                    {
                      required: true,
                      message: "Please enter advance payment amount",
                    },
                  ]}
                >
                  <Input
                    className="ant-picker"
                    type="number"
                    placeholder="Advance payment amount"
                  />
                </Form.Item>
              )}
            </>
          )}
        </div>
        <div className="item">
          <Form.Item>
            <button type="submit" disabled={loading} className="submit-btn">
              {loading
                ? eventId
                  ? "Updating..."
                  : "Adding..."
                : eventId
                  ? "Update Event"
                  : "Add New Event"}
            </button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default AddNewEvent;
